import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
} from "../components/ui/select";
import { toast } from "sonner";
import {
    submitStudentWaitlist,
    submitUniversityWaitlist,
    submitRecruiterWaitlist,
} from "../lib/api";
import { CS_SPECIALIZATIONS } from "../data/home-data";
import { WAITLIST_PAGE } from "../data/waitlist-data";

// ── Validation helpers ─────────────────────────────────────────────────────────
const req = (v) => v != null && String(v).trim() !== "";
const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());
const normalizePhone = (v) => {
    let d = String(v || "").replace(/\D/g, "");
    if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
    if (d.length === 11 && d.startsWith("0")) d = d.slice(1);
    return d;
};
const isMobile = (v) => /^[6-9]\d{9}$/.test(normalizePhone(v));
const isYear = (v) => /^(19|20)\d{2}$/.test(String(v || "").trim());
const isNum = (v) => /^\d+$/.test(String(v || "").trim());
const isPercent = (v) => {
    const s = String(v || "").trim();
    return /^\d{1,3}$/.test(s) && Number(s) >= 0 && Number(s) <= 100;
};
const isName = (v) => /^[A-Za-z][A-Za-z .'-]{1,}$/.test(String(v || "").trim());
const isUrl = (v) => /^https?:\/\/.+\..+/.test(String(v || "").trim());

// ── Reusable field components (with error display) ──────────────────────────────
function TextField({ label, name, form, set, errors, placeholder, type = "text", required, colSpan }) {
    const error = errors[name];
    return (
        <div className={colSpan ? "sm:col-span-2" : ""}>
            <Label>
                {label} {required && "*"}
            </Label>
            <Input
                type={type}
                value={form[name] ?? ""}
                onChange={(e) => set(name, e.target.value)}
                placeholder={placeholder}
                aria-invalid={!!error}
                className={`mt-1.5 h-12 rounded-xl ${error ? "border-red-500 focus-visible:ring-red-500" : ""}`}
            />
            {error && <p data-error="true" className="mt-1 text-xs font-medium text-red-500">{error}</p>}
        </div>
    );
}

function SelectField({ label, name, form, set, errors, options, required, placeholder = WAITLIST_PAGE.selectPlaceholder, colSpan }) {
    const error = errors[name];
    return (
        <div className={colSpan ? "sm:col-span-2" : ""}>
            <Label>
                {label} {required && "*"}
            </Label>
            <Select value={form[name] || ""} onValueChange={(v) => set(name, v)}>
                <SelectTrigger
                    aria-invalid={!!error}
                    className={`mt-1.5 rounded-xl h-12 ${error ? "border-red-500 focus:ring-red-500" : ""}`}
                >
                    <SelectValue placeholder={placeholder} />
                </SelectTrigger>
                <SelectContent>
                    {options.map((o) => (
                        <SelectItem key={o} value={o}>
                            {o}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            {error && <p data-error="true" className="mt-1 text-xs font-medium text-red-500">{error}</p>}
        </div>
    );
}

function CheckboxGroup({ label, name, form, set, errors, options, required }) {
    const error = errors[name];
    const values = form[name] || [];
    const toggle = (opt, checked) =>
        set(name, checked ? [...values, opt] : values.filter((v) => v !== opt));
    return (
        <div className="sm:col-span-2">
            <Label>
                {label} {required && "*"} {WAITLIST_PAGE.checkboxHint}
            </Label>
            <div className={`mt-2 flex flex-wrap gap-2 ${error ? "p-2 rounded-xl ring-1 ring-red-300" : ""}`}>
                {options.map((opt) => (
                    <label
                        key={opt}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full cursor-pointer ${values.includes(opt) ? "bg-ailavender/50" : "bg-gray-50 hover:bg-ailavender/30"}`}
                    >
                        <input
                            type="checkbox"
                            checked={values.includes(opt)}
                            onChange={(e) => toggle(opt, e.target.checked)}
                        />
                        <span className="text-sm">{opt}</span>
                    </label>
                ))}
            </div>
            {error && <p data-error="true" className="mt-1 text-xs font-medium text-red-500">{error}</p>}
        </div>
    );
}

export default function WaitlistPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const initialRole = params.get("role") || "student";

    // Return to the previous page if we arrived from within the app;
    // fall back to Home on a direct/cold load (no in-app history to pop).
    const goBack = () => {
        if (window.history.state && window.history.state.idx > 0) {
            navigate(-1);
        } else {
            navigate("/");
        }
    };
    const [role, setRole] = useState(initialRole);
    const [done, setDone] = useState(false);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({});
    const [errors, setErrors] = useState({});

    useEffect(() => {
        setRole(initialRole);
        setForm({});
        setErrors({});
        setDone(false);
    }, [initialRole]);

    const set = (k, v) => {
        setForm((f) => ({ ...f, [k]: v }));
        // Clear this field's error as soon as the user edits it.
        setErrors((e) => {
            if (!e[k]) return e;
            const next = { ...e };
            delete next[k];
            return next;
        });
    };

    // ── Per-role validation ─────────────────────────────────────────────────────
    const validate = () => {
        const e = {};
        const V = WAITLIST_PAGE.validation;
        const check = (field, value, rules) => {
            for (const [test, msg] of rules) {
                if (!test(value)) {
                    e[field] = msg;
                    return;
                }
            }
        };

        if (role === "student") {
            check("full_name", form.full_name, [[req, V.fullNameRequired], [isName, V.nameInvalid]]);
            check("email", form.email, [[req, V.emailRequired], [isEmail, V.emailInvalid]]);
            check("mobile_number", form.mobile_number, [[req, V.mobileRequired], [isMobile, V.mobileTenDigits]]);
            check("city_location", form.city_location, [[req, V.cityLocationRequired]]);
            if (!(form.tracks_of_interest || []).length) e.tracks_of_interest = V.trackRequired;
            check("discovery_source", form.discovery_source, [[req, V.discoverySourceRequired]]);
        } else if (role === "university") {
            check("institution_name", form.institution_name, [[req, V.institutionNameRequired]]);
            check("institution_type", form.institution_type, [[req, V.institutionTypeRequired]]);
            if (form.institution_type === "Other")
                check("institution_type_other", form.institution_type_other, [[req, V.institutionTypeOtherRequired]]);
            check("year_of_establishment", form.year_of_establishment, [[req, V.establishmentYearRequired], [isYear, V.establishmentYearInvalid], [(v) => Number(v) <= new Date().getFullYear(), V.establishmentFuture]]);
            check("city", form.city, [[req, V.cityRequired]]);
            check("state", form.state, [[req, V.stateRequired]]);
            check("tech_students_count", form.tech_students_count, [[req, V.fieldRequired], [isNum, V.numberInvalid]]);
            check("contact_person_name", form.contact_person_name, [[req, V.contactNameRequired], [isName, V.nameInvalid]]);
            check("designation_role", form.designation_role, [[req, V.designationRequired]]);
            check("official_email", form.official_email, [[req, V.emailRequired], [isEmail, V.emailInvalid]]);
            check("mobile_number", form.mobile_number, [[req, V.mobileRequired], [isMobile, V.mobileTenDigits]]);
            check("has_placement_cell", form.has_placement_cell, [[req, V.placementCellRequired]]);
            check("partnered_with_edtech", form.partnered_with_edtech, [[req, V.placementCellRequired]]);
            check("conducts_campus_drives", form.conducts_campus_drives, [[req, V.placementCellRequired]]);
            check("current_placement_rate", form.current_placement_rate, [[req, V.placementRateRequired], [isPercent, V.percentInvalid]]);
            if (!(form.looking_for || []).length) e.looking_for = V.lookingForRequired;
            check("deployment_timeline", form.deployment_timeline, [[req, V.timelineRequired]]);
            if (req(form.website_url) && !isUrl(form.website_url)) e.website_url = V.urlInvalid;
        } else if (role === "company") {
            check("company_name", form.company_name, [[req, V.companyNameRequired]]);
            check("industry", form.industry, [[req, V.industryRequired]]);
            check("company_registration", form.company_registration, [[req, V.registrationRequired]]);
            check("state", form.state, [[req, V.stateRequired]]);
            check("city", form.city, [[req, V.cityRequired]]);
            check("employee_strength", form.employee_strength, [[req, V.employeeStrengthRequired]]);
            check("contact_name", form.contact_name, [[req, V.contactNameRequired], [isName, V.nameInvalid]]);
            check("designation", form.designation, [[req, V.designationRequired]]);
            check("work_email", form.work_email, [[req, V.workEmailRequired], [isEmail, V.emailInvalid]]);
            check("phone_number", form.phone_number, [[req, V.phoneRequired], [isMobile, V.phoneTenDigits]]);
            check("hiring_volume", form.hiring_volume, [[req, V.hiringVolumeRequired]]);
            check("candidate_experience", form.candidate_experience, [[req, V.candidateExperienceRequired]]);
            if (!(form.preferred_skills || []).length) e.preferred_skills = V.skillRequired;
            check("discovery_source", form.discovery_source, [[req, V.discoverySourceRequired]]);
            if (req(form.official_website) && !isUrl(form.official_website)) e.official_website = V.urlInvalid;
            if (req(form.linkedin_url) && !isUrl(form.linkedin_url)) e.linkedin_url = V.urlInvalid;
        }
        return e;
    };

    const submit = async (e) => {
        e.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length) {
            setErrors(errs);
            // No generic toast — take the user straight to the first field that
            // is incorrect or missing (its inline red message says what to fix).
            setTimeout(() => {
                const el =
                    document.querySelector('[aria-invalid="true"]') ||
                    document.querySelector('[data-error="true"]');
                if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "center" });
                    if (typeof el.focus === "function") el.focus({ preventScroll: true });
                }
            }, 50);
            return;
        }
        setLoading(true);
        try {
            if (role === "student") {
                await submitStudentWaitlist({
                    full_name: form.full_name,
                    email: form.email,
                    mobile_number: form.mobile_number,
                    city_location: form.city_location,
                    tracks_of_interest: form.tracks_of_interest || [],
                    discovery_source: form.discovery_source,
                    discovery_details: form.discovery_details || "",
                });
            } else if (role === "university") {
                const institutionType =
                    form.institution_type === "Other" && form.institution_type_other
                        ? form.institution_type_other
                        : form.institution_type;
                await submitUniversityWaitlist({
                    institution_name: form.institution_name,
                    institution_type: institutionType,
                    affiliated_university: form.affiliated_university || "",
                    year_of_establishment: form.year_of_establishment,
                    city: form.city,
                    state: form.state,
                    website_url: form.website_url || "",
                    tech_students_count: form.tech_students_count,
                    contact_person_name: form.contact_person_name,
                    designation_role: form.designation_role,
                    official_email: form.official_email,
                    mobile_number: form.mobile_number,
                    department: form.department || "",
                    has_placement_cell: form.has_placement_cell,
                    partnered_with_edtech: form.partnered_with_edtech,
                    edtech_partners: form.edtech_partners || "",
                    conducts_campus_drives: form.conducts_campus_drives,
                    current_placement_rate: form.current_placement_rate,
                    looking_for: form.looking_for || [],
                    deployment_timeline: form.deployment_timeline,
                    additional_notes: form.additional_notes || "",
                });
            } else if (role === "company") {
                await submitRecruiterWaitlist({
                    company_name: form.company_name,
                    industry: form.industry,
                    official_website: form.official_website || "",
                    contact_name: form.contact_name,
                    designation: form.designation,
                    work_email: form.work_email,
                    phone_number: form.phone_number,
                    hiring_volume: form.hiring_volume,
                    preferred_skills: form.preferred_skills || [],
                    candidate_experience: form.candidate_experience,
                    company_registration: form.company_registration,
                    address: form.address || "",
                    state: form.state,
                    city: form.city,
                    employee_strength: form.employee_strength,
                    company_logo_url: form.company_logo_url || "",
                    company_overview: form.company_overview || "",
                    linkedin_url: form.linkedin_url || "",
                    social_media_handles: Array.isArray(form.social_media_handles)
                        ? form.social_media_handles.filter(Boolean).join(", ")
                        : form.social_media_handles || "",
                    recruitment_start_date: form.recruitment_start_date || "",
                    discovery_source: form.discovery_source,
                });
            }
            setDone(true);
            toast.success(WAITLIST_PAGE.submit.successToast);
        } catch (err) {
            if (err?.alreadyRegistered) {
                toast.success(WAITLIST_PAGE.submit.alreadyRegisteredToast);
            } else {
                toast.error(err.message || WAITLIST_PAGE.submit.errorToast);
            }
        } finally {
            setLoading(false);
        }
    };

    // Shared props passed to every field component.
    const fp = { form, set, errors };

    if (done) {
        return (
            <div className="min-h-screen pt-32 pb-20 flex items-center justify-center px-4">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center max-w-lg"
                    data-testid="waitlist-success"
                >
                    <CheckCircle2 className="h-16 w-16 text-aiblue mx-auto mb-5" />
                    <h1 className="font-display font-black text-4xl">{WAITLIST_PAGE.success.title}</h1>
                    <p className="mt-4 text-gray-600">
                        {WAITLIST_PAGE.success.message}
                    </p>
                    <Link to="/">
                        <Button
                            className="mt-6 bg-aiblue text-white rounded-full px-7 py-5"
                            data-testid="back-home-btn"
                        >
                            {WAITLIST_PAGE.success.backToHome}
                        </Button>
                    </Link>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen pt-28 pb-20 bg-gradient-to-br from-ailavender/30 via-white to-aipeach/30">
            <div className="max-w-4xl mx-auto px-4 sm:px-8">
                <button
                    type="button"
                    onClick={goBack}
                    className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-aiblue mb-6"
                >
                    <ArrowLeft className="h-4 w-4" /> {WAITLIST_PAGE.header.back}
                </button>
                <h1 className="font-display font-black text-4xl sm:text-5xl tracking-normal leading-none">
                    {WAITLIST_PAGE.header.titleLine1} <br />
                    <span className="text-aiblue">{WAITLIST_PAGE.header.titleLine2}</span>
                </h1>
                <p className="mt-4 text-gray-600">
                    {WAITLIST_PAGE.header.description}
                </p>

                <form
                    key={role}
                    onSubmit={submit}
                    noValidate
                    className="mt-10 space-y-8 bg-white rounded-3xl p-7 sm:p-10 shadow-xl border border-gray-100"
                    data-testid="waitlist-form"
                >
                    {/* Role Selection */}
                    <div>
                        <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                            {WAITLIST_PAGE.roleLabel}
                        </Label>
                        <Select
                            value={role}
                            onValueChange={(v) => {
                                setRole(v);
                                setErrors({});
                            }}
                        >
                            <SelectTrigger className="mt-1.5 rounded-xl h-12" data-testid="role-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {WAITLIST_PAGE.roleOptions.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* STUDENT FORM */}
                    {role === "student" && (
                        <>
                            <div className="space-y-4">
                                <h3 className="font-bold text-lg text-aiblue">{WAITLIST_PAGE.student.sections.personalInfo}</h3>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <TextField {...fp} required label={WAITLIST_PAGE.student.fields.fullName.label} name="full_name" placeholder={WAITLIST_PAGE.student.fields.fullName.placeholder} />
                                    <TextField {...fp} required type="email" label={WAITLIST_PAGE.student.fields.email.label} name="email" placeholder={WAITLIST_PAGE.student.fields.email.placeholder} />
                                    <TextField {...fp} required label={WAITLIST_PAGE.student.fields.mobileNumber.label} name="mobile_number" placeholder={WAITLIST_PAGE.student.fields.mobileNumber.placeholder} />
                                    <TextField {...fp} required label={WAITLIST_PAGE.student.fields.cityLocation.label} name="city_location" placeholder={WAITLIST_PAGE.student.fields.cityLocation.placeholder} />
                                </div>
                            </div>

                            <div className="space-y-4">
                                <h3 className="font-bold text-lg text-aiblue">{WAITLIST_PAGE.student.sections.areaOfInterest}</h3>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <CheckboxGroup {...fp} required label={WAITLIST_PAGE.student.fields.tracksOfInterest.label} name="tracks_of_interest" options={CS_SPECIALIZATIONS.map((s) => s.name)} />
                                </div>
                            </div>

                            <div className="space-y-4">
                                <h3 className="font-bold text-lg text-aiblue">{WAITLIST_PAGE.student.sections.discovery}</h3>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <SelectField {...fp} required label={WAITLIST_PAGE.student.fields.discoverySource.label} name="discovery_source" options={WAITLIST_PAGE.student.options.discoverySource} />
                                    <TextField {...fp} label={WAITLIST_PAGE.student.fields.discoveryDetails.label} name="discovery_details" placeholder={WAITLIST_PAGE.student.fields.discoveryDetails.placeholder} />
                                </div>
                            </div>
                        </>
                    )}

                    {/* UNIVERSITY FORM */}
                    {role === "university" && (
                        <>
                            <div className="space-y-4">
                                <h3 className="font-bold text-lg text-aiblue">{WAITLIST_PAGE.university.sections.institutionInfo}</h3>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <TextField {...fp} required label={WAITLIST_PAGE.university.fields.institutionName.label} name="institution_name" placeholder={WAITLIST_PAGE.university.fields.institutionName.placeholder} />
                                    <div>
                                        <SelectField {...fp} required label={WAITLIST_PAGE.university.fields.institutionType.label} name="institution_type" options={WAITLIST_PAGE.university.options.institutionType} />
                                        {form.institution_type === "Other" && (
                                            <div className="mt-2">
                                                <Input
                                                    value={form.institution_type_other ?? ""}
                                                    onChange={(ev) => set("institution_type_other", ev.target.value)}
                                                    aria-invalid={!!errors.institution_type_other}
                                                    className={`h-12 rounded-xl ${errors.institution_type_other ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                                                    placeholder={WAITLIST_PAGE.university.fields.institutionTypeOther.placeholder}
                                                />
                                                {errors.institution_type_other && <p className="mt-1 text-xs font-medium text-red-500">{errors.institution_type_other}</p>}
                                            </div>
                                        )}
                                    </div>
                                    <TextField {...fp} label={WAITLIST_PAGE.university.fields.affiliatedUniversity.label} name="affiliated_university" placeholder={WAITLIST_PAGE.university.fields.affiliatedUniversity.placeholder} />
                                    <TextField {...fp} required label={WAITLIST_PAGE.university.fields.yearOfEstablishment.label} name="year_of_establishment" placeholder={WAITLIST_PAGE.university.fields.yearOfEstablishment.placeholder} />
                                    <TextField {...fp} required label={WAITLIST_PAGE.university.fields.city.label} name="city" placeholder={WAITLIST_PAGE.university.fields.city.placeholder} />
                                    <TextField {...fp} required label={WAITLIST_PAGE.university.fields.state.label} name="state" placeholder={WAITLIST_PAGE.university.fields.state.placeholder} />
                                    <TextField {...fp} label={WAITLIST_PAGE.university.fields.websiteUrl.label} name="website_url" placeholder={WAITLIST_PAGE.university.fields.websiteUrl.placeholder} />
                                    <TextField {...fp} required label={WAITLIST_PAGE.university.fields.techStudentsCount.label} name="tech_students_count" placeholder={WAITLIST_PAGE.university.fields.techStudentsCount.placeholder} />
                                </div>
                            </div>

                            <div className="space-y-4">
                                <h3 className="font-bold text-lg text-aiblue">{WAITLIST_PAGE.university.sections.pointOfContact}</h3>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <TextField {...fp} required label={WAITLIST_PAGE.university.fields.contactPersonName.label} name="contact_person_name" placeholder={WAITLIST_PAGE.university.fields.contactPersonName.placeholder} />
                                    <TextField {...fp} required label={WAITLIST_PAGE.university.fields.designationRole.label} name="designation_role" placeholder={WAITLIST_PAGE.university.fields.designationRole.placeholder} />
                                    <TextField {...fp} required type="email" label={WAITLIST_PAGE.university.fields.officialEmail.label} name="official_email" placeholder={WAITLIST_PAGE.university.fields.officialEmail.placeholder} />
                                    <TextField {...fp} required label={WAITLIST_PAGE.university.fields.mobileNumber.label} name="mobile_number" placeholder={WAITLIST_PAGE.university.fields.mobileNumber.placeholder} />
                                    <TextField {...fp} label={WAITLIST_PAGE.university.fields.department.label} name="department" placeholder={WAITLIST_PAGE.university.fields.department.placeholder} />
                                </div>
                            </div>

                            <div className="space-y-4">
                                <h3 className="font-bold text-lg text-aiblue">{WAITLIST_PAGE.university.sections.existingSetup}</h3>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <SelectField {...fp} required label={WAITLIST_PAGE.university.fields.hasPlacementCell.label} name="has_placement_cell" options={WAITLIST_PAGE.university.options.yesNo} />
                                    <SelectField {...fp} required label={WAITLIST_PAGE.university.fields.partneredWithEdtech.label} name="partnered_with_edtech" options={WAITLIST_PAGE.university.options.yesNo} />
                                    <TextField {...fp} label={WAITLIST_PAGE.university.fields.edtechPartners.label} name="edtech_partners" placeholder={WAITLIST_PAGE.university.fields.edtechPartners.placeholder} />
                                    <SelectField {...fp} required label={WAITLIST_PAGE.university.fields.conductsCampusDrives.label} name="conducts_campus_drives" options={WAITLIST_PAGE.university.options.yesNo} />
                                    <TextField {...fp} required colSpan label={WAITLIST_PAGE.university.fields.currentPlacementRate.label} name="current_placement_rate" placeholder={WAITLIST_PAGE.university.fields.currentPlacementRate.placeholder} />
                                </div>
                            </div>

                            <div className="space-y-4">
                                <h3 className="font-bold text-lg text-aiblue">{WAITLIST_PAGE.university.sections.partnershipIntent}</h3>
                                <div className="space-y-4">
                                    <CheckboxGroup {...fp} required label={WAITLIST_PAGE.university.fields.lookingFor.label} name="looking_for" options={WAITLIST_PAGE.university.options.lookingFor} />
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <SelectField {...fp} required label={WAITLIST_PAGE.university.fields.deploymentTimeline.label} name="deployment_timeline" options={WAITLIST_PAGE.university.options.deploymentTimeline} />
                                    </div>
                                    <div>
                                        <Label>{WAITLIST_PAGE.university.fields.additionalNotes.label}</Label>
                                        <textarea
                                            value={form.additional_notes ?? ""}
                                            onChange={(e) => set("additional_notes", e.target.value)}
                                            className="mt-1.5 w-full p-3 rounded-xl border border-gray-300 min-h-[100px]"
                                            placeholder={WAITLIST_PAGE.university.fields.additionalNotes.placeholder}
                                        />
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* RECRUITER FORM */}
                    {role === "company" && (
                        <>
                            <div className="space-y-4">
                                <h3 className="font-bold text-lg text-aiblue">{WAITLIST_PAGE.company.sections.companyInfo}</h3>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <TextField {...fp} required label={WAITLIST_PAGE.company.fields.companyName.label} name="company_name" placeholder={WAITLIST_PAGE.company.fields.companyName.placeholder} />
                                    <TextField {...fp} required label={WAITLIST_PAGE.company.fields.industry.label} name="industry" placeholder={WAITLIST_PAGE.company.fields.industry.placeholder} />
                                    <TextField {...fp} colSpan label={WAITLIST_PAGE.company.fields.officialWebsite.label} name="official_website" placeholder={WAITLIST_PAGE.company.fields.officialWebsite.placeholder} />
                                </div>
                            </div>

                            <div className="space-y-4">
                                <h3 className="font-bold text-lg text-aiblue">{WAITLIST_PAGE.company.sections.registrationLocation}</h3>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <TextField {...fp} required colSpan label={WAITLIST_PAGE.company.fields.companyRegistration.label} name="company_registration" placeholder={WAITLIST_PAGE.company.fields.companyRegistration.placeholder} />
                                    <div className="sm:col-span-2">
                                        <Label>{WAITLIST_PAGE.company.fields.address.label}</Label>
                                        <textarea
                                            value={form.address ?? ""}
                                            onChange={(e) => set("address", e.target.value)}
                                            className="mt-1.5 w-full p-3 rounded-xl border border-gray-300 min-h-[80px]"
                                            placeholder={WAITLIST_PAGE.company.fields.address.placeholder}
                                        />
                                    </div>
                                    <TextField {...fp} required label={WAITLIST_PAGE.company.fields.state.label} name="state" placeholder={WAITLIST_PAGE.company.fields.state.placeholder} />
                                    <TextField {...fp} required label={WAITLIST_PAGE.company.fields.city.label} name="city" placeholder={WAITLIST_PAGE.company.fields.city.placeholder} />
                                    <SelectField {...fp} required label={WAITLIST_PAGE.company.fields.employeeStrength.label} name="employee_strength" options={WAITLIST_PAGE.company.options.employeeStrength} />
                                </div>
                            </div>

                            <div className="space-y-4">
                                <h3 className="font-bold text-lg text-aiblue">{WAITLIST_PAGE.company.sections.companyDetails}</h3>
                                <div className="space-y-4">
                                    <div>
                                        <Label>{WAITLIST_PAGE.company.fields.companyOverview.label}</Label>
                                        <textarea
                                            value={form.company_overview ?? ""}
                                            onChange={(e) => set("company_overview", e.target.value)}
                                            className="mt-1.5 w-full p-3 rounded-xl border border-gray-300 min-h-[120px]"
                                            placeholder={WAITLIST_PAGE.company.fields.companyOverview.placeholder}
                                        />
                                    </div>
                                    <TextField {...fp} label={WAITLIST_PAGE.company.fields.linkedinUrl.label} name="linkedin_url" placeholder={WAITLIST_PAGE.company.fields.linkedinUrl.placeholder} />
                                    <div>
                                        <Label>{WAITLIST_PAGE.company.fields.socialMediaHandles.label}</Label>
                                        <div className="mt-2 space-y-2">
                                            {(form.social_media_handles || [""]).map((handle, i) => (
                                                <div key={i} className="flex gap-2">
                                                    <Input
                                                        value={handle}
                                                        onChange={(e) => {
                                                            const arr = [...(form.social_media_handles || [""])];
                                                            arr[i] = e.target.value;
                                                            set("social_media_handles", arr);
                                                        }}
                                                        className="h-12 rounded-xl"
                                                        placeholder={WAITLIST_PAGE.company.fields.socialMediaHandles.placeholder}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const arr = (form.social_media_handles || [""]).filter((_, idx) => idx !== i);
                                                            set("social_media_handles", arr.length ? arr : [""]);
                                                        }}
                                                        className="h-12 w-12 rounded-xl border border-red-200 text-red-500 hover:bg-red-50 flex items-center justify-center font-bold text-lg"
                                                    >
                                                        −
                                                    </button>
                                                </div>
                                            ))}
                                            <button
                                                type="button"
                                                onClick={() => set("social_media_handles", [...(form.social_media_handles || [""]), ""])}
                                                className="flex items-center gap-2 text-sm text-aiblue hover:text-blue-700 font-medium"
                                            >
                                                <span className="h-6 w-6 rounded-full border-2 border-aiblue flex items-center justify-center text-lg leading-none">
                                                    +
                                                </span>{" "}
                                                {WAITLIST_PAGE.company.fields.socialMediaHandles.addAnother}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <h3 className="font-bold text-lg text-aiblue">{WAITLIST_PAGE.company.sections.primaryContact}</h3>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <TextField {...fp} required label={WAITLIST_PAGE.company.fields.contactName.label} name="contact_name" placeholder={WAITLIST_PAGE.company.fields.contactName.placeholder} />
                                    <TextField {...fp} required label={WAITLIST_PAGE.company.fields.designation.label} name="designation" placeholder={WAITLIST_PAGE.company.fields.designation.placeholder} />
                                    <TextField {...fp} required type="email" label={WAITLIST_PAGE.company.fields.workEmail.label} name="work_email" placeholder={WAITLIST_PAGE.company.fields.workEmail.placeholder} />
                                    <TextField {...fp} required label={WAITLIST_PAGE.company.fields.phoneNumber.label} name="phone_number" placeholder={WAITLIST_PAGE.company.fields.phoneNumber.placeholder} />
                                </div>
                            </div>

                            <div className="space-y-4">
                                <h3 className="font-bold text-lg text-aiblue">{WAITLIST_PAGE.company.sections.hiringRequirements}</h3>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <TextField {...fp} required label={WAITLIST_PAGE.company.fields.hiringVolume.label} name="hiring_volume" placeholder={WAITLIST_PAGE.company.fields.hiringVolume.placeholder} />
                                    <SelectField {...fp} required label={WAITLIST_PAGE.company.fields.candidateExperience.label} name="candidate_experience" options={WAITLIST_PAGE.company.options.candidateExperience} />
                                    <CheckboxGroup {...fp} required label={WAITLIST_PAGE.company.fields.preferredSkills.label} name="preferred_skills" options={WAITLIST_PAGE.company.options.preferredSkills} />
                                </div>
                            </div>

                            <div className="space-y-4">
                                <h3 className="font-bold text-lg text-aiblue">{WAITLIST_PAGE.company.sections.hiringTimelineDiscovery}</h3>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <TextField {...fp} type="date" label={WAITLIST_PAGE.company.fields.recruitmentStartDate.label} name="recruitment_start_date" />
                                    <SelectField {...fp} required label={WAITLIST_PAGE.company.fields.discoverySource.label} name="discovery_source" options={WAITLIST_PAGE.company.options.discoverySource} />
                                </div>
                            </div>
                        </>
                    )}

                    <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-aiblue hover:bg-blue-700 text-white rounded-full py-6 font-semibold text-base cta-pulse"
                        data-testid="submit-waitlist-btn"
                    >
                        {loading ? WAITLIST_PAGE.submit.loading : WAITLIST_PAGE.submit.idle}
                    </Button>
                </form>
            </div>
        </div>
    );
}
